import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../../../contexts/AppContext';
import { useEstabFormContext } from './Context';
import { PaginaService } from './PaginaService';
import { FileService } from '../../../../components/files/service';
import { convertFileToBase64 } from '../../../../components/files/functions';
import { GALLERY_ACCEPT, PaginaGaleria } from './PaginaGaleria';
import { PageContent, PaginaConteudo } from './PaginaConteudo';
import { SettingsRow, SettingsSection } from './SettingsGroup';
import { CircleAlert, CircleCheck, ExternalLink } from 'lucide-react';

const service = new PaginaService();
const fileService = new FileService();

// Template padrao exigido pela API na criacao; a escolha de template ainda nao existe nesta tela.
const DEFAULT_TEMPLATE_ID = 'generico';
const SLUG_MIN = 3;
const SLUG_MAX = 80;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error';

// Mesmas regras do backend (normalizePageName); hifen final e mantido enquanto digita.
const normalizeSlugInput = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-+/, '')
        .slice(0, SLUG_MAX);

const finalizeSlug = (value: string) => normalizeSlugInput(value).replace(/-+$/g, '');

const getLocalSlugError = (slug: string) => {
    if (!slug) return 'Informe o endereço da página.';
    if (slug.length < SLUG_MIN) return `Use pelo menos ${SLUG_MIN} caracteres.`;
    if (!SLUG_PATTERN.test(slug)) return 'Use apenas letras minúsculas, números e hífens.';
    return '';
};

const EMPTY_CONTENT: PageContent = { coverImageId: '', title: '', description: '', about: '', aboutImageId: '', highlights: [], testimonials: [] };

// Remove itens totalmente vazios (linha adicionada e nao usada); parciais sao barrados no salvar.
const cleanContent = (content: PageContent): PageContent => ({
    ...content,
    title: content.title.trim(),
    description: content.description.trim(),
    about: content.about.trim(),
    highlights: content.highlights
        .map((item) => ({ ...item, title: item.title.trim(), text: item.text.trim() }))
        .filter((item) => item.title || item.text),
    testimonials: content.testimonials
        .map((item) => ({ name: item.name.trim(), text: item.text.trim() }))
        .filter((item) => item.name || item.text),
});

const getContentError = (content: PageContent) => {
    if (content.highlights.some((item) => !item.title)) return 'Informe o título de todos os diferenciais.';
    if (content.testimonials.some((item) => !item.name || !item.text)) return 'Informe o nome e o texto de todos os depoimentos.';
    return '';
};

export function PaginaEstabelecimento() {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { name } = useEstabFormContext();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pendingUploads, setPendingUploads] = useState(0);
    const [pendingPhotos, setPendingPhotos] = useState(0);
    const uploading = pendingUploads > 0 || pendingPhotos > 0;
    const [content, setContent] = useState<PageContent>(EMPTY_CONTENT);
    const [savedContent, setSavedContent] = useState<PageContent>(EMPTY_CONTENT);
    const [imagesIds, setImagesIds] = useState<string[]>([]);
    const [savedImagesIds, setSavedImagesIds] = useState<string[]>([]);
    const [hasPage, setHasPage] = useState(false);
    const [planBlocked, setPlanBlocked] = useState(false);
    const [slug, setSlug] = useState('');
    const [savedSlug, setSavedSlug] = useState('');
    const [status, setStatus] = useState<SlugStatus>('idle');
    const [statusMessage, setStatusMessage] = useState('');
    const checkSeq = useRef(0);

    const publicBase = `${window.location.host}/page/`;
    const finalSlug = finalizeSlug(slug);
    const isDirty = finalSlug !== savedSlug || JSON.stringify(imagesIds) !== JSON.stringify(savedImagesIds) || JSON.stringify(content) !== JSON.stringify(savedContent) || !hasPage;

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const { data } = await service.findByEstab();
                const images = Array.isArray(data?.imagesIds) ? data.imagesIds : [];
                setImagesIds(images);
                setSavedImagesIds(images);
                const loaded: PageContent = {
                    coverImageId: data?.imagesIdsHeader?.[0] || '',
                    title: data?.title || '',
                    description: data?.description || '',
                    about: data?.about || '',
                    aboutImageId: data?.aboutImageId || '',
                    highlights: (data?.highlights || []).map((item: any) => ({ icon: item.icon || '', title: item.title || '', text: item.text || '' })),
                    testimonials: (data?.testimonials || []).map((item: any) => ({ name: item.name || '', text: item.text || '' })),
                };
                setContent(loaded);
                setSavedContent(loaded);
                if (data?.pageName) {
                    setHasPage(true);
                    setSlug(data.pageName);
                    setSavedSlug(data.pageName);
                }
            } catch (error: any) {
                // A aba fica montada desde a abertura da tela; sem o recurso no plano, so informa na propria aba.
                if (error?.response?.status === 403) {
                    setPlanBlocked(true);
                } else {
                    errorSnackbar('Não foi possível carregar a configuração da página.');
                }
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, []);

    // Sugere um slug a partir do nome quando ainda nao ha pagina cadastrada.
    useEffect(() => {
        if (loading || hasPage || slug || !name) return;
        setSlug(finalizeSlug(name));
    }, [loading, hasPage, name]);

    useEffect(() => {
        if (loading || planBlocked) return;

        const localError = getLocalSlugError(finalSlug);
        if (localError) {
            setStatus('invalid');
            setStatusMessage(localError);
            return;
        }

        if (hasPage && finalSlug === savedSlug) {
            setStatus('available');
            setStatusMessage('Este é o endereço atual da página.');
            return;
        }

        const seq = ++checkSeq.current;
        setStatus('checking');
        setStatusMessage('Verificando disponibilidade...');

        const timeoutId = window.setTimeout(async () => {
            try {
                const { data } = await service.checkPageName(finalSlug);
                if (seq !== checkSeq.current) return;
                if (data.available) {
                    setStatus('available');
                    setStatusMessage('Endereço disponível.');
                } else {
                    setStatus(data.reason === 'taken' ? 'taken' : 'invalid');
                    setStatusMessage(
                        data.reason === 'taken'
                            ? 'Este endereço já está em uso por outro estabelecimento.'
                            : data.message || 'Endereço inválido.',
                    );
                }
            } catch (error) {
                if (seq !== checkSeq.current) return;
                setStatus('error');
                setStatusMessage('Não foi possível verificar o endereço agora.');
            }
        }, 450);

        return () => window.clearTimeout(timeoutId);
    }, [finalSlug, loading, planBlocked, hasPage, savedSlug]);

    const uploadImages = async (files: File[]) => {
        if (saving || !files.length) return;
        const images = files.filter((file) => GALLERY_ACCEPT.includes(file.type));
        if (images.length < files.length) {
            errorSnackbar('Alguns arquivos foram ignorados. Use imagens JPG, PNG, WebP ou GIF.');
        }
        if (!images.length) return;

        // Placeholders por arquivo; cada envio concluido vira miniatura na ordem de chegada.
        setPendingUploads((count) => count + images.length);
        let failed = 0;
        for (const file of images) {
            try {
                const content = await convertFileToBase64(file);
                const { data } = await fileService.upload(content, file.name, file.size, file.type, new Date(file.lastModified));
                if (!data?._id) throw new Error('Arquivo sem identificador');
                setImagesIds((current) => [...current, data._id]);
            } catch {
                failed += 1;
            } finally {
                setPendingUploads((count) => Math.max(0, count - 1));
            }
        }
        if (failed) errorSnackbar(`${failed} ${failed === 1 ? 'foto não foi enviada' : 'fotos não foram enviadas'}. Tente novamente.`);
    };

    const uploadPhoto = async (file: File) => {
        if (!GALLERY_ACCEPT.includes(file.type)) {
            errorSnackbar('Use uma imagem JPG, PNG, WebP ou GIF.');
            return null;
        }
        setPendingPhotos((count) => count + 1);
        try {
            const fileContent = await convertFileToBase64(file);
            const { data } = await fileService.upload(fileContent, file.name, file.size, file.type, new Date(file.lastModified));
            if (!data?._id) throw new Error('Arquivo sem identificador');
            return data._id as string;
        } catch {
            errorSnackbar('Não foi possível enviar a foto. Tente novamente.');
            return null;
        } finally {
            setPendingPhotos((count) => Math.max(0, count - 1));
        }
    };

    const handleSave = async () => {
        if (status !== 'available' || saving || uploading) return;

        const cleaned = cleanContent(content);
        const contentError = getContentError(cleaned);
        if (contentError) {
            errorSnackbar(contentError);
            return;
        }

        try {
            setSaving(true);
            const { coverImageId, ...fields } = cleaned;
            const payload = {
                pageName: finalSlug,
                active: true,
                imagesIds,
                ...fields,
                // Cadastros antigos podem ter varias capas; so regrava a lista se a capa foi alterada.
                ...(coverImageId !== savedContent.coverImageId ? { imagesIdsHeader: coverImageId ? [coverImageId] : [] } : {}),
            };
            if (hasPage) {
                await service.update(payload);
            } else {
                await service.create({ ...payload, templateId: DEFAULT_TEMPLATE_ID });
            }
            setHasPage(true);
            setSlug(finalSlug);
            setSavedSlug(finalSlug);
            setSavedImagesIds([...imagesIds]);
            setContent(cleaned);
            setSavedContent(cleaned);
            successSnackbar('Página salva.');
        } catch (error: any) {
            const responseStatus = error?.response?.status;
            const message = error?.response?.data?.message;
            if (responseStatus === 409) {
                setStatus('taken');
                setStatusMessage('Este endereço já está em uso por outro estabelecimento.');
            }
            errorSnackbar(
                responseStatus === 403
                    ? 'O plano atual não inclui a página do estabelecimento.'
                    : typeof message === 'string'
                      ? message
                      : 'Não foi possível salvar a página.',
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <span className="loading loading-spinner loading-sm"></span>;
    }

    if (planBlocked) {
        return (
            <SettingsSection title="Página do estabelecimento">
                <SettingsRow stacked>
                    <div className="py-1 text-sm text-base-content/70">
                        O plano atual não inclui a página do estabelecimento. Faça upgrade para publicar seu link.
                    </div>
                </SettingsRow>
            </SettingsSection>
        );
    }

    const statusTone =
        status === 'available'
            ? 'text-success'
            : status === 'taken' || status === 'invalid' || status === 'error'
              ? 'text-error'
              : 'text-base-content/55';
    const StatusIcon =
        status === 'available'
            ? CircleCheck
            : status === 'checking' || status === 'idle'
              ? null
              : CircleAlert;

    const photosLabel = `${imagesIds.length} ${imagesIds.length === 1 ? 'foto' : 'fotos'}`;

    return (
        <div className="space-y-6">
            <p className="px-1 text-sm text-base-content/60">
                A página fica publicada automaticamente no endereço salvo, com os dados do estabelecimento.
            </p>
            <SettingsSection
                title="Endereço da página"
                footer={
                    <span className={`inline-flex items-start gap-1.5 ${statusTone}`} aria-live="polite">
                        {status === 'checking' && <span className="loading loading-spinner mt-[0.2rem] w-3 shrink-0" aria-hidden="true" />}
                        {StatusIcon && <StatusIcon className="mt-[0.2rem] text-[0.7rem]" aria-hidden="true" />}
                        <span>
                            {statusMessage ||
                                'Letras minúsculas, números e hífens. Este será o link compartilhado com clientes.'}
                        </span>
                    </span>
                }
            >
                <SettingsRow label="Slug" htmlFor="pageSlug">
                    <div className="flex min-w-0 items-center">
                        <span className="hidden shrink-0 text-sm text-base-content/45 sm:inline">{publicBase}</span>
                        <input
                            id="pageSlug"
                            type="text"
                            inputMode="url"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={SLUG_MAX}
                            value={slug}
                            onChange={(e) => setSlug(normalizeSlugInput(e.target.value))}
                            onBlur={() => setSlug(finalizeSlug(slug))}
                            className="input estab-field min-w-0 flex-1 sm:!ml-0 sm:!w-auto sm:!pl-0.5"
                            placeholder="meu-estabelecimento"
                            aria-invalid={status === 'taken' || status === 'invalid'}
                        />
                    </div>
                </SettingsRow>
                <SettingsRow inline>
                    <div className="min-w-0 truncate text-xs text-base-content/55">
                        {publicBase}
                        <span className="font-medium text-base-content/80">{finalSlug || 'seu-endereco'}</span>
                    </div>
                    {savedSlug && (
                        <a
                            className="estab-link-button shrink-0"
                            href={`/page/${encodeURIComponent(savedSlug)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Abrir página
                            <ExternalLink className="text-[0.7rem]" aria-hidden="true" />
                        </a>
                    )}
                </SettingsRow>
            </SettingsSection>

            <PaginaConteudo
                content={content}
                disabled={saving}
                onChange={(patch) => setContent((current) => ({ ...current, ...patch }))}
                onUploadImage={uploadPhoto}
            />

            <SettingsSection
                title={imagesIds.length ? `Galeria de fotos · ${photosLabel}` : 'Galeria de fotos'}
                footer="JPG, PNG, WebP ou GIF. Toque em uma foto para ampliar. As alterações vão para a página ao clicar em Salvar página."
            >
                <PaginaGaleria
                    imagesIds={imagesIds}
                    pendingUploads={pendingUploads}
                    disabled={saving}
                    onAddFiles={(files) => void uploadImages(files)}
                    onRemove={(index) => setImagesIds((current) => current.filter((_, position) => position !== index))}
                />
            </SettingsSection>

            <div className="estab-savebar">
                <button
                    type="button"
                    className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                    disabled={saving || uploading || status !== 'available' || !isDirty}
                    onClick={handleSave}
                >
                    {saving && <span className="loading loading-spinner loading-sm"></span>}
                    {uploading ? 'Aguardando envio…' : 'Salvar página'}
                </button>
            </div>
        </div>
    );
}
